import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Loader2,
  ReceiptText,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DateRangeFilter } from "@/components/ui/date-range-filter";
import { FilterMenu } from "@/components/ui/filter-menu";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useBillingOrders,
  useBillingOrderDetail,
  useCapturedAfterRelease,
  useTransactionDetail,
  useTransactions,
} from "@/hooks/useTransactions";
import {
  DetailRow,
  DetailSection,
  EmptyState,
  LoadingState,
} from "@/components/shared/detail-view";
import { cn } from "@/lib/utils";
import type {
  BillingHoldState,
  BillingOrderDetail,
  Transaction,
  TransactionDetail,
  TransactionStatus,
} from "@/types/transactions";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";

const PAGE_SIZE = 20;
const EMPTY_VALUE = "-";

const STATUS_VARIANTS: Partial<
  Record<TransactionStatus, "success" | "critical" | "warning" | "neutral">
> = {
  completed: "success",
  failed: "critical",
  initiated: "warning",
  processing: "neutral",
  created: "warning",
  pending: "warning",
  awaiting_payment: "warning",
  paid: "success",
  no_charge: "success",
  abandoned: "neutral",
  expired: "critical",
  cancelled: "critical",
};

const APPOINTMENT_STATUS_VARIANTS: Record<
  string,
  "success" | "critical" | "warning" | "neutral"
> = {
  completed: "success",
  confirmed: "success",
  cancelled: "critical",
  pending: "warning",
  soft_reserved: "warning",
};

const LEGACY_STATUS_TABS = [
  { value: "", label: "All" },
  { value: "initiated", label: "Created" },
  { value: "completed", label: "Captured" },
  { value: "failed", label: "Failed" },
] as const;

const BILLING_STATUS_TABS = [
  { value: "", label: "All" },
  { value: "created", label: "Created" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "no_charge", label: "No charge" },
  { value: "failed", label: "Failed" },
  { value: "abandoned", label: "Abandoned" },
] as const;

const METHOD_OPTIONS = [
  { value: "all", label: "All modes" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "wallet", label: "Wallet" },
  { value: "netbanking", label: "Netbanking" },
  { value: "cash", label: "Cash" },
];

const PAYMENT_MODE_LABELS: Record<string, string> = {
  upi: "UPI",
  card: "Card",
  wallet: "Wallet",
  netbanking: "Netbanking",
  cash: "Cash",
};

const formatPaymentMode = (value?: string | null) => {
  if (!value) return EMPTY_VALUE;
  const key = value.toLowerCase();
  return PAYMENT_MODE_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value?: number | null) => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value);
};

const display = (value?: string | number | null) =>
  value === null || value === undefined || value === "" ? EMPTY_VALUE : value;

const formatLabel = (value?: string | null) =>
  value ? value.replaceAll("_", " ") : EMPTY_VALUE;

/** Time of day only, e.g. "4:17 pm" — slot holds last minutes, not days. */
const formatTime = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const HOLD_STATE_BADGES: Record<
  BillingHoldState,
  { label: string; variant: "warning" | "success" | "neutral" | "outline" }
> = {
  held: { label: "Held", variant: "warning" },
  confirmed: { label: "Confirmed", variant: "success" },
  released: { label: "Released", variant: "neutral" },
  not_started: { label: "Not started", variant: "outline" },
};

const HoldStateBadge = ({ state }: { state?: BillingHoldState }) => {
  if (!state) return <>{EMPTY_VALUE}</>;
  const { label, variant } = HOLD_STATE_BADGES[state] ?? {
    label: formatLabel(state),
    variant: "outline",
  };
  return <Badge variant={variant}>{label}</Badge>;
};

type TopTab = "payments" | "billing" | "invoices";
type BillingView = "orders" | "captured_after_release";

export const Transactions = () => {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canViewBillingOrders = hasPermission(
    CONSOLE_PERMISSIONS.VIEW_BILLING_INVOICES
  );
  const [topTab, setTopTab] = useState<TopTab>("payments");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  // The list row travels with the selection: patient, service and
  // appointment status come from the list API, not the order detail.
  const [selectedBillingOrder, setSelectedBillingOrder] = useState<{
    id: string;
    row?: Transaction;
  } | null>(null);
  const [billingView, setBillingView] = useState<BillingView>("orders");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setCurrentPage(1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, methodFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (!canViewBillingOrders && topTab === "billing") {
      setTopTab("payments");
      setBillingView("orders");
      setSelectedBillingOrder(null);
    }
  }, [canViewBillingOrders, topTab]);

  const params = useMemo(
    () => ({
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter && { status: statusFilter }),
      ...(methodFilter && { payment_method: methodFilter }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
      page: currentPage,
      page_size: PAGE_SIZE,
    }),
    [currentPage, dateFrom, dateTo, debouncedSearch, methodFilter, statusFilter]
  );

  const { data, isLoading, isError, error } = useTransactions(params);
  const showCapturedAfterRelease =
    topTab === "billing" && billingView === "captured_after_release";
  const billingOrders = useBillingOrders(
    params,
    topTab === "billing" && !showCapturedAfterRelease && canViewBillingOrders
  );
  const billingOrderDetail = useBillingOrderDetail(
    selectedBillingOrder?.id,
    canViewBillingOrders
  );
  // Count only, for the tab badge; the panel fetches its own pages.
  const capturedAfterReleaseCount = useCapturedAfterRelease(
    { page: 1, page_size: 1 },
    topTab === "billing" && canViewBillingOrders
  ).data?.count;
  const {
    data: selectedTransaction,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useTransactionDetail(selectedTransactionId);

  const activeData = topTab === "billing" ? billingOrders.data : data;
  const activeLoading =
    topTab === "billing" ? billingOrders.isLoading : isLoading;
  const activeError = topTab === "billing" ? billingOrders.isError : isError;
  const activeErrorValue = topTab === "billing" ? billingOrders.error : error;
  const statusTabs =
    topTab === "billing" ? BILLING_STATUS_TABS : LEGACY_STATUS_TABS;
  const hasActiveFilters =
    Boolean(searchText) ||
    Boolean(statusFilter) ||
    Boolean(methodFilter) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!dateFrom && !dateTo) return undefined;
    return {
      from: dateFrom ? new Date(dateFrom) : undefined,
      to: dateTo ? new Date(dateTo) : undefined,
    };
  }, [dateFrom, dateTo]);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateFrom(range?.from ? format(range.from, "yyyy-MM-dd") : "");
    setDateTo(range?.to ? format(range.to, "yyyy-MM-dd") : "");
  };

  const clearFilters = () => {
    setSearchText("");
    setDebouncedSearch("");
    setStatusFilter("");
    setMethodFilter("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  if (selectedTransactionId) {
    return (
      <TransactionDetailView
        transaction={selectedTransaction}
        isLoading={isDetailLoading}
        isError={isDetailError}
        onBack={() => setSelectedTransactionId(null)}
      />
    );
  }

  if (selectedBillingOrder) {
    return (
      <BillingOrderDetailView
        order={billingOrderDetail.data}
        listRow={selectedBillingOrder.row}
        isLoading={billingOrderDetail.isLoading}
        isError={billingOrderDetail.isError}
        onBack={() => setSelectedBillingOrder(null)}
      />
    );
  }

  return (
    <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="z-h1 text-foreground">Transactions</CardTitle>
            <CardDescription className="text-muted-foreground">
              Review payment attempts, gateway status, and invoices from the
              console.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 flex flex-col gap-4 p-0">
        {/* Top-level tabs: Payments / Invoices */}
        <div className="px-6">
          <Tabs
            value={topTab}
            onValueChange={(next) => {
              setTopTab(next as TopTab);
              setBillingView("orders");
              setStatusFilter("");
              setCurrentPage(1);
            }}
          >
            <TabsList>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              {canViewBillingOrders ? (
                <TabsTrigger value="billing">Billing 2.0 Orders</TabsTrigger>
              ) : null}
              <TabsTrigger value="invoices">Invoices</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {topTab === "invoices" ? (
          <InvoicesWip />
        ) : (
          <>
            {/* Status tabs */}
            <div className="px-6">
              <Tabs
                value={
                  showCapturedAfterRelease
                    ? "captured_after_release"
                    : statusFilter || "all"
                }
                onValueChange={(next) => {
                  if (next === "captured_after_release") {
                    setBillingView("captured_after_release");
                    return;
                  }
                  setBillingView("orders");
                  setStatusFilter(next === "all" ? "" : next);
                }}
              >
                <TabsList variant="line">
                  {statusTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value || "all"}
                      value={tab.value || "all"}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                  {topTab === "billing" ? (
                    <TabsTrigger value="captured_after_release">
                      Captured after release
                      {capturedAfterReleaseCount ? (
                        <Badge variant="critical">
                          {capturedAfterReleaseCount}
                        </Badge>
                      ) : null}
                    </TabsTrigger>
                  ) : null}
                </TabsList>
              </Tabs>
            </div>

            {showCapturedAfterRelease ? (
              <CapturedAfterReleasePanel
                onOpenOrder={(id) => setSelectedBillingOrder({ id })}
              />
            ) : (
              <>
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6">
                  <div className="flex items-center gap-2 flex-1 flex-wrap">
                    <SearchInput
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      onClear={() => setSearchText("")}
                      placeholder={
                        topTab === "billing"
                          ? "Search patient, phone, Zarity ID, or payment ID"
                          : "Search payment, patient, or appointment"
                      }
                      containerClassName="w-full sm:w-80"
                    />

                    <FilterMenu
                      label="Payment mode"
                      options={METHOD_OPTIONS}
                      value={methodFilter || "all"}
                      onValueChange={(next) =>
                        setMethodFilter(next === "all" ? "" : next)
                      }
                    />

                    <DateRangeFilter
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      placeholder="Date range"
                    />

                    {hasActiveFilters && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={clearFilters}
                        className="z-caption h-auto p-0 text-muted-foreground hover:text-foreground"
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                </div>

                {/* Table */}
                <div className="flex-1 min-h-0 px-6 flex flex-col">
                  {activeLoading ? (
                    <LoadingState label="Loading transactions..." />
                  ) : activeError ? (
                    <EmptyState
                      icon={AlertCircle}
                      title="Failed to load transactions"
                      description={
                        (activeErrorValue as Error)?.message ||
                        "Please try again in a moment."
                      }
                    />
                  ) : !activeData || activeData.results.length === 0 ? (
                    <EmptyState
                      icon={ReceiptText}
                      title="No transactions found"
                      description="Try adjusting your filters."
                    />
                  ) : (
                    <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-sm">
                      <Table>
                        <TableHeader className="sticky top-0 z-10 bg-card">
                          <TableRow className="hover:bg-card">
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground pl-4">
                              Created on
                            </TableHead>
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                              Payment ID
                            </TableHead>
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                              Payment mode
                            </TableHead>
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                              Patient
                            </TableHead>
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                              Amount
                            </TableHead>
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                              Status
                            </TableHead>
                            {topTab === "billing" ? (
                              <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                                Hold
                              </TableHead>
                            ) : null}
                            <TableHead className="z-label uppercase tracking-wide text-muted-foreground pr-4" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeData.results.map((transaction) => (
                            <TransactionRow
                              key={transaction.id}
                              transaction={transaction}
                              showHold={topTab === "billing"}
                              onView={
                                topTab === "billing"
                                  ? () =>
                                      setSelectedBillingOrder({
                                        id: transaction.id,
                                        row: transaction,
                                      })
                                  : () =>
                                      setSelectedTransactionId(transaction.id)
                              }
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {activeData ? (
                  <ListFooter
                    page={currentPage}
                    count={activeData.count}
                    noun={topTab === "billing" ? "orders" : "payments"}
                    onPageChange={setCurrentPage}
                  />
                ) : null}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const ListFooter = ({
  page,
  count,
  noun,
  onPageChange,
}: {
  page: number;
  count: number;
  noun: string;
  onPageChange: (page: number) => void;
}) => {
  if (count === 0) return null;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  return (
    <div className="flex items-center justify-between px-6 py-3 border-t">
      <span className="z-secondary text-muted-foreground">
        Showing{" "}
        <span className="z-numeric font-medium text-foreground">
          {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, count)}
        </span>{" "}
        of{" "}
        <span className="z-numeric font-medium text-foreground">{count}</span>{" "}
        {noun}
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            Previous
          </Button>
          <span className="z-secondary text-muted-foreground px-2">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Read-only list of payments the gateway captured after the slot hold had
 * already been released. Ops refunds these in Razorpay; the console only
 * surfaces them.
 */
const CapturedAfterReleasePanel = ({
  onOpenOrder,
}: {
  onOpenOrder: (billingOrderId: string) => void;
}) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error } = useCapturedAfterRelease(
    { page, page_size: PAGE_SIZE },
    true
  );

  return (
    <>
      <div className="px-6">
        <Alert>
          <AlertCircle />
          <AlertDescription>
            Money arrived after the slot was released. Refund it in the Razorpay
            dashboard.
          </AlertDescription>
        </Alert>
      </div>

      <div className="flex-1 min-h-0 px-6 flex flex-col">
        {isLoading ? (
          <LoadingState label="Loading payments..." />
        ) : isError ? (
          <EmptyState
            icon={AlertCircle}
            title="Failed to load payments"
            description={
              (error as Error)?.message || "Please try again in a moment."
            }
          />
        ) : !data || data.results.length === 0 ? (
          <EmptyState
            icon={ReceiptText}
            title="No payments captured after release"
            description="Payments that arrive after their slot was released will appear here."
          />
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-sm">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow className="hover:bg-card">
                  <TableHead className="z-label uppercase tracking-wide text-muted-foreground pl-4">
                    Received
                  </TableHead>
                  <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                    Payment ID
                  </TableHead>
                  <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                    Gateway order ID
                  </TableHead>
                  <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                    Amount
                  </TableHead>
                  <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                    Path
                  </TableHead>
                  <TableHead className="z-label uppercase tracking-wide text-muted-foreground pr-4" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((payment) => {
                  const billingOrderId = payment.billing_order_id;
                  // Legacy-path payments have no Billing order to open.
                  const openOrder = billingOrderId
                    ? () => onOpenOrder(billingOrderId)
                    : undefined;
                  return (
                    <TableRow
                      key={payment.id}
                      className={cn(
                        "bg-card hover:bg-muted/50",
                        openOrder && "cursor-pointer"
                      )}
                      onClick={openOrder}
                    >
                      <TableCell className="whitespace-nowrap z-body text-muted-foreground pl-4">
                        {formatDateTime(payment.received_at)}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate z-numeric text-foreground">
                        {display(payment.provider_payment_id)}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate z-numeric text-foreground">
                        {display(payment.gateway_order_id)}
                      </TableCell>
                      <TableCell className="z-numeric tabular-nums text-foreground">
                        {payment.amount === null
                          ? EMPTY_VALUE
                          : formatAmount(Number(payment.amount))}
                      </TableCell>
                      <TableCell>
                        <Badge variant="neutral">
                          {payment.owner === "billing" ? "Billing" : "Legacy"}
                        </Badge>
                      </TableCell>
                      <TableCell className="pr-4 text-right">
                        {openOrder ? (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto gap-0.5 p-0 text-primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              openOrder();
                            }}
                          >
                            Order
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {data ? (
        <ListFooter
          page={page}
          count={data.count}
          noun="payments"
          onPageChange={setPage}
        />
      ) : null}
    </>
  );
};

const InvoicesWip = () => (
  <div className="flex flex-1 min-h-0 items-center justify-center px-6">
    <EmptyState
      icon={FileText}
      title="Invoices — coming soon"
      description="We're wiring up the invoices APIs. Once the backend ships, you'll see issued invoices, their payment status, and downloadable PDFs here."
    />
  </div>
);

const BillingOrderDetailView = ({
  order,
  listRow,
  isLoading,
  isError,
  onBack,
}: {
  order?: BillingOrderDetail;
  /** Present when opened from the Orders list; absent from other entry points. */
  listRow?: Transaction;
  isLoading: boolean;
  isError: boolean;
  onBack: () => void;
}) => {
  if (isLoading) return <LoadingState label="Loading Billing order..." />;
  if (isError || !order) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Failed to load Billing order"
        description="Please try again in a moment."
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-6">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="-ml-2 gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </Button>
        <DetailSection title="Order">
          <DetailRow
            label="Amount"
            value={formatAmount(order.amount_paise / 100)}
          />
          {listRow ? (
            <>
              <DetailRow
                label="Patient"
                value={[listRow.patient_name, listRow.patient_zarity_user_id]
                  .filter(Boolean)
                  .join(" · ")}
              />
              <DetailRow label="Service" value={listRow.service_name} />
              <DetailRow
                label="Appointment"
                value={listRow.zarity_appointment_id}
                mono
                copyable
              />
              <DetailRow
                label="Appointment status"
                value={
                  listRow.appointment_status ? (
                    <Badge
                      variant={
                        APPOINTMENT_STATUS_VARIANTS[
                          listRow.appointment_status.toLowerCase()
                        ] ?? "neutral"
                      }
                      className="capitalize"
                    >
                      {formatLabel(listRow.appointment_status)}
                    </Badge>
                  ) : null
                }
              />
            </>
          ) : null}
        </DetailSection>
        <DetailSection title="Payment resolution">
          <DetailRow label="Order ID" value={order.id} mono copyable />
          <DetailRow
            label="Gateway order ID"
            value={order.gateway_order_id}
            mono
            copyable
          />
          <DetailRow
            label="Status"
            value={
              <Badge variant={STATUS_VARIANTS[order.status] || "neutral"}>
                {formatLabel(order.status)}
              </Badge>
            }
          />
          <DetailRow
            label="Resolution attempts"
            value={order.resolution_attempts}
          />
          <DetailRow
            label="Last provider check"
            value={formatDateTime(order.resolution_last_checked_at)}
          />
          <DetailRow
            label="Appointment ID"
            value={order.source_type === "appointment" ? order.source_id : null}
            mono
            copyable
          />
        </DetailSection>
        <DetailSection title="Slot hold">
          <DetailRow
            label="Hold state"
            value={<HoldStateBadge state={order.hold_state} />}
          />
          {order.payment_hold_expires_at ? (
            <DetailRow
              label="Hold end time"
              value={formatDateTime(order.payment_hold_expires_at)}
            />
          ) : null}
          {order.payment_released_at ? (
            <DetailRow
              label="Release time"
              value={formatDateTime(order.payment_released_at)}
            />
          ) : null}
        </DetailSection>
        <DetailSection title="Resolution history">
          {order.events.length ? (
            <div className="space-y-3">
              {order.events.map((event) => (
                <div
                  key={event.id}
                  className="rounded-lg border border-border/60 p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="z-body font-medium">
                      {formatLabel(event.from_status)} →{" "}
                      {formatLabel(event.to_status)}
                    </span>
                    <span className="z-caption text-muted-foreground">
                      {formatDateTime(event.at)}
                    </span>
                  </div>
                  <p className="z-caption mt-1 text-muted-foreground">
                    {event.reason || "No reason recorded"} ·{" "}
                    {formatLabel(event.source)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="z-secondary text-muted-foreground">
              No resolution attempts recorded.
            </p>
          )}
        </DetailSection>
      </div>
    </div>
  );
};

const STATUS_HERO_ICON: Partial<
  Record<TransactionStatus, { Icon: typeof CheckCircle2; tone: string }>
> = {
  completed: { Icon: CheckCircle2, tone: "bg-success/10 text-success" },
  failed: { Icon: XCircle, tone: "bg-destructive/10 text-destructive" },
  initiated: { Icon: Clock, tone: "bg-warning/10 text-warning" },
  processing: { Icon: Clock, tone: "bg-muted text-muted-foreground" },
};

const TransactionDetailView = ({
  transaction,
  isLoading,
  isError,
  onBack,
}: {
  transaction?: TransactionDetail;
  isLoading: boolean;
  isError: boolean;
  onBack: () => void;
}) => {
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !transaction) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
        <AlertCircle className="mb-2 h-10 w-10 opacity-40" />
        <p className="z-body font-medium">Failed to load payment details.</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
      </div>
    );
  }

  const status = transaction.status;
  const statusVariant = STATUS_VARIANTS[status] || "neutral";
  const heroIcon = STATUS_HERO_ICON[status] ?? STATUS_HERO_ICON.processing;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-6">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-1">
        {/* Back link */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="-ml-2 gap-1 px-2 text-primary hover:bg-primary/5 hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </Button>

        {/* Amount hero */}
        <Card className="border border-border/60 shadow-sm">
          <CardContent className="flex items-center gap-4 px-6 py-5">
            <div
              className={cn(
                "flex h-14 w-14 shrink-0 items-center justify-center rounded-lg",
                heroIcon.tone
              )}
            >
              <heroIcon.Icon className="h-7 w-7" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="z-h1 tabular-nums text-foreground">
                  {formatAmount(transaction.amount)}
                </p>
                <Badge variant={statusVariant} className="capitalize">
                  {status}
                </Badge>
              </div>
              {transaction.patient_name ||
              transaction.patient_zarity_user_id ? (
                <p className="z-body text-foreground">
                  {transaction.patient_name}
                  {transaction.patient_zarity_user_id ? (
                    <span className="z-caption font-mono text-muted-foreground">
                      {transaction.patient_name ? " · " : ""}
                      {transaction.patient_zarity_user_id}
                    </span>
                  ) : null}
                </p>
              ) : null}
              <p className="z-caption text-muted-foreground">
                Created on:{" "}
                <span className="text-foreground">
                  {formatDateTime(transaction.payment_created_at)}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Transaction — gateway-facing facts for reconciliation */}
        <DetailSection title="Transaction">
          <DetailRow
            label="Payment ID"
            value={transaction.payment_id || transaction.id}
            mono
            copyable
          />
          <DetailRow
            label="Payment method"
            value={
              transaction.payment_method || transaction.payment_details ? (
                <span className="flex flex-col gap-0.5">
                  {transaction.payment_method ? (
                    <span className="z-body capitalize text-foreground">
                      {transaction.payment_method}
                    </span>
                  ) : null}
                  {transaction.payment_details ? (
                    <span className="z-caption text-muted-foreground">
                      {transaction.payment_details}
                    </span>
                  ) : null}
                </span>
              ) : null
            }
          />
        </DetailSection>

        {/* Appointment — what this payment was for */}
        <DetailSection title="Appointment">
          <DetailRow
            label="Appointment ID"
            value={transaction.zarity_appointment_id}
            mono
            copyable
          />
          <DetailRow
            label="Status"
            value={
              transaction.appointment_status ? (
                <Badge
                  variant={
                    APPOINTMENT_STATUS_VARIANTS[
                      transaction.appointment_status.toLowerCase()
                    ] ?? "neutral"
                  }
                  className="capitalize"
                >
                  {formatLabel(transaction.appointment_status)}
                </Badge>
              ) : null
            }
          />
          <DetailRow
            label="Type"
            value={formatLabel(transaction.appointment_type)}
            valueClassName="capitalize"
          />
          <DetailRow label="Service" value={transaction.service_name} />
          <DetailRow label="Staff" value={transaction.doctor_name} />
        </DetailSection>

        {/* Refund */}
        <Card className="border border-border/60 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between gap-3 border-b border-border/60 px-6 py-4 space-y-0">
            <div className="space-y-0.5">
              <CardTitle className="z-h3 text-foreground">Refund</CardTitle>
              <CardDescription className="z-caption text-muted-foreground">
                Refunds are not available from the console yet.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              aria-disabled="true"
              title="Refunds will be available soon"
            >
              Issue refund
            </Button>
          </CardHeader>
          <CardContent className="px-6 py-5">
            <p className="z-secondary text-muted-foreground">
              Refund details are not available in the console yet.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const TransactionRow = ({
  transaction,
  showHold = false,
  onView,
}: {
  transaction: Transaction;
  showHold?: boolean;
  onView?: () => void;
}) => {
  const holdUntil =
    transaction.hold_state === "held"
      ? formatTime(transaction.payment_hold_expires_at)
      : null;

  return (
    <TableRow
      className={cn(
        "group bg-card transition-colors hover:bg-muted/50",
        onView && "cursor-pointer"
      )}
      onClick={onView}
    >
      <TableCell className="whitespace-nowrap z-body text-muted-foreground pl-4">
        {formatDateTime(transaction.payment_created_at)}
      </TableCell>
      <TableCell className="max-w-[220px] truncate z-numeric text-foreground">
        {display(transaction.payment_id || transaction.id)}
      </TableCell>
      <TableCell className="z-body text-muted-foreground">
        {transaction.payment_method ? (
          <Badge variant="neutral">
            {formatPaymentMode(transaction.payment_method)}
          </Badge>
        ) : (
          EMPTY_VALUE
        )}
      </TableCell>
      <TableCell className="z-body text-foreground">
        <div className="flex flex-col items-start gap-1">
          <span>{display(transaction.patient_name)}</span>
          {transaction.patient_zarity_user_id ? (
            <Badge variant="neutral" className="z-caption font-normal">
              {transaction.patient_zarity_user_id}
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="z-numeric tabular-nums text-foreground">
        {formatAmount(transaction.amount)}
      </TableCell>
      <TableCell>
        <Badge
          variant={STATUS_VARIANTS[transaction.status] || "neutral"}
          className="capitalize"
        >
          {transaction.status}
        </Badge>
      </TableCell>
      {showHold ? (
        <TableCell>
          <div className="flex flex-col items-start gap-1">
            <HoldStateBadge state={transaction.hold_state} />
            {holdUntil ? (
              <span className="z-caption text-muted-foreground">
                until {holdUntil}
              </span>
            ) : null}
          </div>
        </TableCell>
      ) : null}
      <TableCell className="pr-4 text-right">
        {onView ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto gap-0.5 p-0 text-primary"
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            Details
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
};
